import { z } from "zod/v4";
import { type QueryRowType, type Transaction } from "@rocicorp/zero";
import type { InferUITools, UIDataTypes, UIMessage as AgentUIMessage } from "ai";

import { defineAgent } from "../../ai/agent-definition/agent";
import { AgentSession } from "../../ai/agent-session";
import { AskQuestionsTool } from "../../ai/agent/ask-questions";
import { projectTool } from "../../ai/agent/tool-lifecycle";
import { defineStep, defineWorkflow } from "../../ai/define-workflow";
import { Group } from "../../auth/group";
import { defineQuery } from "../../zero/transformer";
import { withZeroTransaction } from "../../zero/utils";
import { zql, type Context as ZeroContext } from "../../zero/schema";
import { NutritionProgram } from "../program";
import { MealPlanningSession } from "../meal-planning-session";
import { MealPlanningSessionScopePolicy } from "../meal-planning-session/scope-policy";
import { Nutrient } from "../nutrient";

export namespace MealPlanningMemberContextAgent {
  export const nutritionGoalSchema = z.object({
    type: z.union([z.literal("weight_loss"), z.literal("maintenance"), z.literal("muscle_gain")]),
    energyOffsetKcal: z.number(),
  });
  export type NutritionGoal = z.infer<typeof nutritionGoalSchema>;

  const scheduleSlotContextSchema = z.object({
    mealScheduleId: z.uuid(),
    mealScheduleSlotId: z.uuid(),
    scheduleName: z.string().min(1),
    name: z.string().min(1),
    time: z.string().min(1),
  });
  export type ScheduleSlotContext = z.infer<typeof scheduleSlotContextSchema>;

  export const nutritionContextSchema = z.object({
    dailyNutritionTargets: Nutrient.targetsSchema(),
    nutritionGoal: nutritionGoalSchema.nullable(),
  });
  export type NutritionContext = z.infer<typeof nutritionContextSchema>;

  const includedSlotTargetResultSchema = z.object({
    mealScheduleId: z.uuid(),
    mealScheduleSlotId: z.uuid(),
    nutritionProgramDayId: z.uuid().nullable(),
    status: z.literal("included"),
    nutrientTargets: Nutrient.targetsSchema({
      requiredKeys: Nutrient.macroEnergyKeys,
    }).describe(
      "Schedule-slot nutrient target allocation. Supported target shapes are exact, between, atLeast, and atMost; do not use max for nutrient targets.",
    ),
  });
  const skippedSlotTargetResultSchema = z.object({
    mealScheduleId: z.uuid(),
    mealScheduleSlotId: z.uuid(),
    nutritionProgramDayId: z.uuid().nullable(),
    status: z.literal("skipped"),
  });
  const slotTargetResultSchema = z.discriminatedUnion("status", [
    includedSlotTargetResultSchema,
    skippedSlotTargetResultSchema,
  ]);

  const baseToolResultSchema = z.object({
    preferences: z.array(z.string().min(1)),
  });
  export const toolResultSchema = baseToolResultSchema.extend({
    slotTargets: z.array(slotTargetResultSchema),
  });
  export type ToolResult = z.infer<typeof toolResultSchema>;

  export const resultSchema = toolResultSchema.extend({
    nutritionGoal: nutritionGoalSchema.nullable(),
  });
  export type Result = z.infer<typeof resultSchema>;

  function toolResultSchemaForContext(context: Context | null | undefined) {
    return baseToolResultSchema.extend({
      slotTargets: z.array(slotTargetResultSchema).min(context?.scheduleSlots.length ? 1 : 0),
    });
  }

  export type CreateUnavailableReason =
    | "wrong_user"
    | "member_not_in_session"
    | "planner_exists"
    | "member_context_exists";

  export type EditUnavailableReason =
    | "member_context_not_found"
    | "wrong_user"
    | "member_not_in_session"
    | "planner_exists";

  export function getCreateUnavailableReason(args: {
    actorUserId: string;
    paramsUserId: string;
    session: Pick<MealPlanningSession.Info, "id" | "group"> | null | undefined;
    plannerSessionExists: boolean;
    memberContextSessionExists: boolean;
  }): CreateUnavailableReason | null {
    if (args.paramsUserId !== args.actorUserId) return "wrong_user";
    if (!Group.hasMember(args.session?.group, args.paramsUserId)) return "member_not_in_session";
    if (args.plannerSessionExists) return "planner_exists";
    if (args.memberContextSessionExists) return "member_context_exists";
    return null;
  }

  export function getEditUnavailableReason(args: {
    actorUserId: string;
    linkedSessionUserId: string | null | undefined;
    session: Pick<MealPlanningSession.Info, "id" | "group"> | null | undefined;
    plannerSessionExists: boolean;
  }): EditUnavailableReason | null {
    if (!args.linkedSessionUserId) return "member_context_not_found";
    if (args.linkedSessionUserId !== args.actorUserId) return "wrong_user";
    if (!Group.hasMember(args.session?.group, args.linkedSessionUserId)) {
      return "member_not_in_session";
    }
    if (args.plannerSessionExists) return "planner_exists";
    return null;
  }

  function saveMemberContextToolForContext(context: Context | null | undefined) {
    const schema = toolResultSchemaForContext(context);
    return projectTool({
      description: "Save the current member's meal-planning preferences for this session.",
      inputSchema: schema,
      outputSchema: schema,
      lifecycle: { continueAfterTool: false },
    });
  }

  export const tools = {
    askQuestions: AskQuestionsTool.define(
      "Ask the member focused questions to clarify their meal-planning preferences. Only call this tool once per message. When allowCustom is true, do NOT include 'Other' or similar options in the options array - the UI will automatically add a custom input option.",
    ),
    saveMemberContext: projectTool({
      description: "Save the current member's meal-planning preferences for this session.",
      inputSchema: toolResultSchema,
      outputSchema: toolResultSchema,
      lifecycle: { continueAfterTool: false },
    }),
  };

  export function toolsForContext(context: Context | null | undefined) {
    return { ...tools, saveMemberContext: saveMemberContextToolForContext(context) };
  }

  export type ChatUIMessage = AgentUIMessage<unknown, UIDataTypes, InferUITools<typeof tools>>;

  export const paramsSchema = z.object({
    mealPlanningSessionId: z.uuid(),
    userId: z.uuid(),
    initialMessageId: z.uuid(),
  });
  export type Params = z.infer<typeof paramsSchema>;

  export const contextSchema = z.object({
    mealPlanningSessionId: z.uuid(),
    userId: z.uuid(),
    nutritionContext: nutritionContextSchema.nullable().optional().default(null),
    scheduleSlots: z.array(scheduleSlotContextSchema).optional().default([]),
  });
  export type Context = z.infer<typeof contextSchema>;

  function nutritionGoalFromGoalOffset(goalOffset: number): NutritionGoal {
    if (goalOffset === 0) return { type: "maintenance", energyOffsetKcal: 0 };
    return {
      type: goalOffset < 0 ? "weight_loss" : "muscle_gain",
      energyOffsetKcal: goalOffset,
    };
  }

  export async function getNutritionContext(
    tx: Transaction,
    ctx: ZeroContext,
    params: { userId: string; dateStart: Temporal.PlainDate; dateEnd: Temporal.PlainDate },
  ): Promise<NutritionContext | null> {
    const programs = await tx.run(
      NutritionProgram.inDateRange.fn({
        ctx,
        args: {
          dateRange: { dateStart: params.dateStart, dateEnd: params.dateEnd },
          userId: params.userId,
        },
      }),
    );
    if (programs.length === 0) return null;

    const dates: Temporal.PlainDate[] = [];
    let current = params.dateStart;
    while (Temporal.PlainDate.compare(current, params.dateEnd) <= 0) {
      dates.push(current);
      current = current.add({ days: 1 });
    }

    const days = dates.map((date) => NutritionProgram.dayForDate(programs, date));
    if (days.some((day) => day === undefined)) return null;

    const firstDay = days[0];
    if (!firstDay || days.some((day) => day?.id !== firstDay.id)) return null;

    const programInfo = await tx.run(
      NutritionProgram.onDate.fn({
        ctx,
        args: { userId: params.userId, date: params.dateStart.toString() },
      }),
    );
    return {
      dailyNutritionTargets: firstDay.nutrientTargets as Nutrient.Targets,
      nutritionGoal: programInfo?.expenditure
        ? nutritionGoalFromGoalOffset(programInfo.expenditure.goalOffset)
        : null,
    };
  }

  export const definition = defineAgent({
    agentType: "meal-planning-member-context",
    version: 1,
    paramsSchema,
    context: {
      schema: contextSchema,
      resolve: async ({ tx, ctx, args: { params } }) => {
        const session = await MealPlanningSession.requireById(tx, params.mealPlanningSessionId);
        const scheduleSlots = session.scheduleConfig
          ? session.scheduleConfig.data.assignments.flatMap((assignment) =>
              assignment.slots.map((slot) => ({
                mealScheduleId: assignment.mealScheduleId,
                mealScheduleSlotId: slot.mealScheduleSlotId,
                scheduleName: assignment.name,
                name: slot.name,
                time: slot.time.toString(),
              })),
            )
          : [];
        return {
          mealPlanningSessionId: params.mealPlanningSessionId,
          userId: params.userId,
          nutritionContext: await getNutritionContext(tx, ctx, {
            userId: params.userId,
            dateStart: session.dateStart,
            dateEnd: session.dateEnd,
          }),
          scheduleSlots,
        };
      },
    },
    chat: { tools, resolveTools: ({ context }) => toolsForContext(context) },
    canCreate: async ({ tx, ctx, args }) => {
      const session = await MealPlanningSession.fetchById(tx, args.params.mealPlanningSessionId);
      return (
        getCreateUnavailableReason({
          actorUserId: ctx.actor.userId,
          paramsUserId: args.params.userId,
          session,
          plannerSessionExists: await MealPlanningAgentInternal.plannerSessionExists(
            tx,
            args.params.mealPlanningSessionId,
          ),
          memberContextSessionExists: await MealPlanningSession.memberContextExists(tx, {
            mealPlanningSessionId: args.params.mealPlanningSessionId,
            userId: args.params.userId,
          }),
        }) === null
      );
    },
    canEdit: async ({ tx, ctx, args }) => {
      const linked = await MealPlanningAgentInternal.getMemberContextSessionById(
        tx,
        args.sessionId,
      );
      const session = linked
        ? await MealPlanningSession.fetchById(tx, linked.mealPlanningSessionId)
        : undefined;
      return (
        getEditUnavailableReason({
          actorUserId: ctx.actor.userId,
          linkedSessionUserId: linked?.userId,
          session,
          plannerSessionExists: linked
            ? await MealPlanningAgentInternal.plannerSessionExists(tx, linked.mealPlanningSessionId)
            : false,
        }) === null
      );
    },
    viewable: ({ exists }, actor) =>
      exists("mealPlanningMemberContextAgentSession", (linked) =>
        linked
          .where("userId", "=", actor.userId)
          .whereExists("base", (base) =>
            base.where("agentType", "=", "meal-planning-member-context"),
          ),
      ),
  }).onCreate(async ({ tx, args: { sessionId, params } }) => {
    const timeNow = Temporal.Now.instant();
    await tx.mutate.mealPlanningMemberContextAgentSession.insert({
      id: sessionId,
      mealPlanningSessionId: params.mealPlanningSessionId,
      userId: params.userId,
      timeCreated: timeNow,
      timeUpdated: timeNow,
    });
    await AgentSession.insertInitialAssistantMessage(tx, {
      sessionId,
      messageId: params.initialMessageId,
      timeNow,
    });
    return { initialMessageId: params.initialMessageId, userId: params.userId };
  });

  export const mySession = defineQuery(
    z.object({ mealPlanningSessionId: z.uuid() }),
    ({ args, ctx }) =>
      zql.mealPlanningMemberContextAgentSession
        .where("mealPlanningSessionId", "=", args.mealPlanningSessionId)
        .where("userId", "=", ctx.actor.userId)
        .related("base")
        .one(),
  );

  export const results = defineQuery(
    z.object({ mealPlanningSessionId: z.uuid() }),
    ({ args, ctx }) =>
      zql.mealPlanningMemberContextAgentSession
        .where("mealPlanningSessionId", "=", args.mealPlanningSessionId)
        .whereExists("mealPlanningSession", (session) =>
          session.where((eb) => MealPlanningSessionScopePolicy.viewable(eb, ctx.actor)),
        ),
  );

  export const sessionById = defineQuery(z.object({ sessionId: z.uuid() }), ({ args }) =>
    zql.mealPlanningMemberContextAgentSession.where("id", "=", args.sessionId).one(),
  );

  export const saveResult = withZeroTransaction(
    z.object({ sessionId: z.uuid(), result: toolResultSchema }),
    async ({ tx, ctx, args }) => {
      const linked = await MealPlanningAgentInternal.getMemberContextSessionById(
        tx,
        args.sessionId,
      );
      if (!linked) throw new Error("Meal planning member-context agent session not found");
      if (linked.userId !== ctx.actor.userId) {
        throw new Error("Cannot save another user's meal planning member context");
      }
      const base = await tx.run(zql.agentSession.where("id", "=", args.sessionId).one());
      if (base?.agentType !== definition.agentType) {
        throw new Error("Agent session is not a meal planning member-context session");
      }
      const context = contextSchema.parse(base.context);
      const session = await MealPlanningSession.fetchById(tx, linked.mealPlanningSessionId);
      if (!session) throw new Error("Meal planning session not found");
      if (!Group.hasMember(session?.group, linked.userId)) {
        throw new Error("User is not a member of this meal planning session");
      }
      if (await MealPlanningAgentInternal.plannerSessionExists(tx, linked.mealPlanningSessionId)) {
        throw new Error("Member context cannot be changed after planner session creation");
      }
      const data = MealPlanningSession.memberContextDataSchema.parse({
        preferences: args.result.preferences,
        slotTargets: args.result.slotTargets,
        nutritionGoal: context.nutritionContext?.nutritionGoal ?? null,
      });
      await MealPlanningSession.upsertMemberContextForMember(tx, {
        session,
        userId: linked.userId,
        data,
      });
    },
  );
}

export namespace MealPlanningSessionPlannerAgent {
  export type CreateUnavailableReason =
    | "no_members"
    | "planner_exists"
    | "member_contexts_incomplete"
    | "planned_meals_exist";

  export function getCreateUnavailableReason(args: {
    session: Pick<MealPlanningSession.Info, "group" | "plannedMeals"> | null | undefined;
    plannerSessionExists: boolean;
    memberContextsComplete: boolean;
  }): CreateUnavailableReason | null {
    const { session } = args;
    if (!session || !session.group || session.group.members.length === 0) return "no_members";
    if (args.plannerSessionExists) return "planner_exists";
    if (!args.memberContextsComplete) return "member_contexts_incomplete";
    if (session.plannedMeals.length > 0) return "planned_meals_exist";
    return null;
  }

  const eventSchema = z.object({
    id: z.uuid(),
    date: z.iso.date(),
    assignments: z.array(
      z.object({
        id: z.uuid(),
        plateId: z.uuid(),
      }),
    ),
  });

  const proposedMealSchema = z.object({
    id: z.uuid(),
    mealScheduleId: z.uuid(),
    mealScheduleSlotId: z.uuid(),
    name: z.string(),
    plates: z.array(
      z.object({
        id: z.uuid(),
        userId: z.uuid(),
      }),
    ),
    events: z.array(eventSchema),
  });

  export type ProposedMeal = z.infer<typeof proposedMealSchema>;

  export const tools = {
    proposePlan: projectTool({
      description: "Propose draft planned meals for the meal-planning session.",
      inputSchema: z.object({ meals: z.array(proposedMealSchema).min(1) }),
      outputSchema: z.object({ meals: z.array(proposedMealSchema).min(1) }),
      lifecycle: { continueAfterTool: false },
    }),
  };

  export const paramsSchema = z.object({
    mealPlanningSessionId: z.uuid(),
    initialMessageId: z.uuid(),
  });
  export type Params = z.infer<typeof paramsSchema>;

  export const contextSchema = z.object({ mealPlanningSessionId: z.uuid() });
  export type Context = z.infer<typeof contextSchema>;

  export const definition = defineAgent({
    agentType: "meal-planning-session-planner",
    version: 1,
    paramsSchema,
    context: {
      schema: contextSchema,
      resolve: async ({ args: { params } }) => ({
        mealPlanningSessionId: params.mealPlanningSessionId,
      }),
    },
    chat: { tools },
    canCreate: async ({ tx, ctx, args }) =>
      MealPlanningAgentInternal.canCreatePlannerSession(tx, ctx, args.params.mealPlanningSessionId),
    canEdit: async ({ tx, ctx, args }) => {
      const linked = await MealPlanningAgentInternal.getPlannerSessionById(tx, args.sessionId);
      if (!linked) return false;
      return MealPlanningSessionScopePolicy.canEditContents(
        tx,
        ctx.actor,
        linked.mealPlanningSessionId,
      );
    },
    viewable: ({ exists }, actor) =>
      exists("mealPlanningSessionPlannerAgentSession", (linked) =>
        linked.whereExists("mealPlanningSession", (session) =>
          session.where((sessionEb) => MealPlanningSessionScopePolicy.viewable(sessionEb, actor)),
        ),
      ),
  }).onCreate(async ({ tx, ctx, args: { sessionId, params } }) => {
    const timeNow = Temporal.Now.instant();
    await tx.mutate.mealPlanningSessionPlannerAgentSession.insert({
      id: sessionId,
      mealPlanningSessionId: params.mealPlanningSessionId,
      timeCreated: timeNow,
      timeUpdated: timeNow,
    });
    await AgentSession.insertInitialAssistantMessage(tx, {
      sessionId,
      messageId: params.initialMessageId,
      timeNow,
    });
    return { initialMessageId: params.initialMessageId, userId: ctx.actor.userId };
  });

  export const session = defineQuery(
    z.object({ mealPlanningSessionId: z.uuid() }),
    ({ args, ctx }) =>
      zql.mealPlanningSessionPlannerAgentSession
        .where("mealPlanningSessionId", "=", args.mealPlanningSessionId)
        .whereExists("mealPlanningSession", (mealPlanningSession) =>
          mealPlanningSession.where((eb) => MealPlanningSessionScopePolicy.viewable(eb, ctx.actor)),
        )
        .related("base")
        .related("workflowExecutions", (workflowLink) =>
          workflowLink.related("base", (workflow) =>
            workflow.related("steps", (step) => step.orderBy("sortIndex", "asc")),
          ),
        )
        .one(),
  );

  export const sessionById = defineQuery(z.object({ sessionId: z.uuid() }), ({ args }) =>
    zql.mealPlanningSessionPlannerAgentSession
      .where("id", "=", args.sessionId)
      .related("base")
      .one(),
  );

  export const linkWorkflowExecution = withZeroTransaction(
    z.object({
      id: z.uuid(),
      mealPlanningSessionId: z.uuid(),
      plannedMealId: z.uuid(),
      agentSessionId: z.uuid(),
      timeCreated: z.instanceof(Temporal.Instant),
      timeUpdated: z.instanceof(Temporal.Instant),
    }),
    async ({ tx, args }) => {
      await tx.mutate.mealPlanningWorkflowExecution.insert(args);
    },
  );

  export type ByMealPlanningSessionId = NonNullable<QueryRowType<typeof session>>;

  export namespace GenerateMealWorkflow {
    export const contextSchema = z.object({
      mealPlanningSessionId: z.uuid(),
      mealId: z.uuid(),
      agentSessionId: z.uuid(),
      initiatedByUserId: z.uuid(),
    });
    export type Context = z.infer<typeof contextSchema>;

    const emptyInputSchema = z.object({});
    const generateMealOutputSchema = z.object({ mealId: z.uuid(), generated: z.literal(true) });
    const verificationSchema = z.object({ ok: z.boolean() });

    export const generateMealStep = defineStep({
      name: "generateMeal",
      inputSchema: emptyInputSchema,
      outputSchema: generateMealOutputSchema,
    });
    export const resolveItemsStep = defineStep({
      name: "resolveItems",
      inputSchema: emptyInputSchema,
      outputSchema: verificationSchema,
    });
    export const adjustItemsForTargetsStep = defineStep({
      name: "adjustItemsForTargets",
      inputSchema: emptyInputSchema,
      outputSchema: verificationSchema,
    });

    export const definition = defineWorkflow({
      workflowType: "MealPlanningGenerateMeal",
      version: 1,
      contextSchema,
      steps: {
        generateMeal: generateMealStep,
        resolveItems: resolveItemsStep,
        adjustItemsForTargets: adjustItemsForTargetsStep,
      },
      initialStep: "generateMeal",
      transitions: {
        generateMeal: () => ({ to: "resolveItems", input: {} }),
        resolveItems: (output) => (output.ok ? null : { to: "adjustItemsForTargets", input: {} }),
        adjustItemsForTargets: null,
      },
      canCreate: async ({ tx, ctx, args }) =>
        MealPlanningSessionScopePolicy.canEditContents(
          tx,
          ctx.actor,
          args.context.mealPlanningSessionId,
        ),
      canEdit: async ({ tx, ctx, args }) => {
        const linked = await MealPlanningAgentInternal.getWorkflowLink(tx, args.executionId);
        return linked
          ? MealPlanningSessionScopePolicy.canEditContents(
              tx,
              ctx.actor,
              linked.mealPlanningSessionId,
            )
          : false;
      },
      viewable: ({ exists }, actor) =>
        exists("mealPlanningWorkflowExecutions", (workflow) =>
          workflow.whereExists("mealPlanningSession", (session) =>
            session.where((eb) => MealPlanningSessionScopePolicy.viewable(eb, actor)),
          ),
        ),
    });
  }
}

namespace MealPlanningAgentInternal {
  export function getMemberContextSessionById(tx: Transaction, sessionId: string) {
    return tx.run(zql.mealPlanningMemberContextAgentSession.where("id", "=", sessionId).one());
  }

  export function getPlannerSessionById(tx: Transaction, sessionId: string) {
    return tx.run(zql.mealPlanningSessionPlannerAgentSession.where("id", "=", sessionId).one());
  }

  export async function plannerSessionExists(tx: Transaction, mealPlanningSessionId: string) {
    const row = await tx.run(
      zql.mealPlanningSessionPlannerAgentSession
        .where("mealPlanningSessionId", "=", mealPlanningSessionId)
        .one(),
    );
    return row !== undefined;
  }

  export async function memberContextSessionExists(
    tx: Transaction,
    mealPlanningSessionId: string,
    userId: string,
  ) {
    const row = await tx.run(
      zql.mealPlanningMemberContextAgentSession
        .where("mealPlanningSessionId", "=", mealPlanningSessionId)
        .where("userId", "=", userId)
        .one(),
    );
    return row !== undefined;
  }

  export function getWorkflowLink(tx: Transaction, executionId: string) {
    return tx.run(zql.mealPlanningWorkflowExecution.where("id", "=", executionId).one());
  }

  export async function canCreatePlannerSession(
    tx: Transaction,
    ctx: ZeroContext,
    mealPlanningSessionId: string,
  ) {
    const session = await MealPlanningSession.fetchById(tx, mealPlanningSessionId);
    const createUnavailableReason = MealPlanningSessionPlannerAgent.getCreateUnavailableReason({
      session,
      plannerSessionExists: await plannerSessionExists(tx, mealPlanningSessionId),
      memberContextsComplete: await MealPlanningSession.memberContextsComplete(
        tx,
        mealPlanningSessionId,
      ),
    });
    if (createUnavailableReason) return false;
    return MealPlanningSessionScopePolicy.canEditContents(tx, ctx.actor, mealPlanningSessionId);
  }
}
